import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, CheckCircle, Eye, EyeOff, RefreshCw, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: password, 4: success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [loading, setLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  // Countdown timer
  useEffect(() => {
    if (step === 2 && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, step]);

  // Focus on first OTP input when step changes to 2
  useEffect(() => {
    if (step === 2 && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [step]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      
      if (response.data.sessionId) {
        setSessionId(response.data.sessionId);
        setStep(2);
        toast.success(response.data.message || 'Verification code sent to your email');
      } else {
        // Generic response (user may not exist)
        toast.success(response.data.message || 'If an account exists, you will receive a verification code');
        // Still move to step 2 if we got a response
        setStep(2);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are filled
    if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
      handleOtpSubmit(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6);
        if (digits.length > 0) {
          const newOtp = [...otp];
          for (let i = 0; i < Math.min(digits.length, 6); i++) {
            newOtp[i] = digits[i];
          }
          setOtp(newOtp);
          
          // Focus on next empty input or last input
          const nextIndex = Math.min(digits.length, 5);
          inputRefs.current[nextIndex]?.focus();
          
          // Auto-submit if complete
          if (digits.length === 6) {
            handleOtpSubmit(digits);
          }
        }
      });
    }
  };

  const handleOtpSubmit = async (otpCode = null) => {
    const codeToSubmit = otpCode || otp.join('');
    if (codeToSubmit.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/api/auth/verify-reset-otp', {
        sessionId,
        otp: codeToSubmit
      });

      if (response.data.verified) {
        toast.success('OTP verified successfully');
        setStep(3);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
      // Clear OTP inputs on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      
      if (response.data.sessionId) {
        setSessionId(response.data.sessionId);
      }
      
      setTimeLeft(600); // Reset timer
      setOtp(['', '', '', '', '', '']); // Clear current OTP
      inputRefs.current[0]?.focus();
      toast.success('Verification code resent to your email');
    } catch (error) {
      toast.error('Failed to resend verification code');
    } finally {
      setIsResending(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/auth/reset-password', {
        sessionId,
        newPassword
      });

      toast.success('Password reset successfully');
      setStep(4);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Email Input
  const renderEmailStep = () => (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
          <Mail className="h-8 w-8 text-primary-600" />
        </div>
        <h2 className="text-3xl font-bold text-secondary-900 mb-2">
          Forgot Password?
        </h2>
        <p className="text-secondary-600">
          No worries, we'll send you reset instructions
        </p>
      </div>

      <form onSubmit={handleEmailSubmit} className="bg-white shadow-lg rounded-lg p-8">
        <div className="space-y-6">
          <div>
            <label className="form-label">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder="Enter your email"
              required
            />
            <p className="mt-2 text-sm text-secondary-500">
              We'll send a verification code to this email address
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || !email}
            className="w-full btn-primary btn-lg"
          >
            {loading ? (
              <LoadingSpinner size="sm" text="Sending..." />
            ) : (
              'Send Verification Code'
            )}
          </button>

          <Link
            to="/login"
            className="w-full flex justify-center items-center py-2 px-4 text-sm font-medium text-secondary-600 hover:text-secondary-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </form>
    </div>
  );

  // Step 2: OTP Verification
  const renderOtpStep = () => (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
          <Shield className="h-8 w-8 text-primary-600" />
        </div>
        <h2 className="text-3xl font-bold text-secondary-900 mb-2">
          Verify Your Email
        </h2>
        <p className="text-secondary-600">
          We've sent a verification code to
        </p>
        <p className="text-secondary-900 font-medium">{email}</p>
      </div>

      <div className="bg-white shadow-lg rounded-lg p-8">
        <div className="space-y-6">
          {/* OTP Input Fields */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-3 text-center">
              Please enter the 6-digit verification code
            </label>
            <div className="flex justify-center space-x-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-primary-500 focus:outline-none transition-colors"
                  disabled={loading}
                />
              ))}
            </div>
          </div>

          {/* Timer and Resend */}
          <div className="text-center">
            <p className="text-sm text-secondary-600 mb-3">
              Code expires in: {formatTime(timeLeft)}
            </p>
            
            {timeLeft > 0 ? (
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={isResending || timeLeft > 540} // Can resend after 1 minute
                className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Resending...
                  </>
                ) : timeLeft > 540 ? (
                  `Resend available in ${formatTime(540 - (600 - timeLeft))}`
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Resend Code
                  </>
                )}
              </button>
            ) : (
              <div className="text-red-600 text-sm">
                Code expired, please request a new one
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => handleOtpSubmit()}
            disabled={loading || otp.some(digit => digit === '') || timeLeft === 0}
            className="w-full btn-primary btn-lg"
          >
            {loading ? (
              <LoadingSpinner size="sm" text="Verifying..." />
            ) : (
              'Verify Code'
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              setStep(1);
              setOtp(['', '', '', '', '', '']);
              setTimeLeft(600);
            }}
            disabled={loading}
            className="w-full flex justify-center items-center py-2 px-4 text-sm font-medium text-secondary-600 hover:text-secondary-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Email
          </button>
        </div>
      </div>
    </div>
  );

  // Step 3: New Password
  const renderPasswordStep = () => (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
          <Lock className="h-8 w-8 text-primary-600" />
        </div>
        <h2 className="text-3xl font-bold text-secondary-900 mb-2">
          Set New Password
        </h2>
        <p className="text-secondary-600">
          Choose a strong password for your account
        </p>
      </div>

      <form onSubmit={handlePasswordSubmit} className="bg-white shadow-lg rounded-lg p-8">
        <div className="space-y-6">
          {/* New Password Field */}
          <div>
            <label className="form-label">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="form-input pr-10"
                placeholder="Enter new password"
                required
                minLength={8}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-secondary-400" />
                ) : (
                  <Eye className="h-5 w-5 text-secondary-400" />
                )}
              </button>
            </div>
            <p className="mt-1 text-sm text-secondary-500">
              Must be at least 8 characters
            </p>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label className="form-label">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input pr-10"
                placeholder="Confirm new password"
                required
                minLength={8}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-secondary-400" />
                ) : (
                  <Eye className="h-5 w-5 text-secondary-400" />
                )}
              </button>
            </div>
          </div>

          {/* Password Match Indicator */}
          {confirmPassword && (
            <div className={`text-sm ${newPassword === confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
              {newPassword === confirmPassword ? (
                <span className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Passwords match
                </span>
              ) : (
                'Passwords do not match'
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            className="w-full btn-primary btn-lg"
          >
            {loading ? (
              <LoadingSpinner size="sm" text="Resetting..." />
            ) : (
              'Reset Password'
            )}
          </button>
        </div>
      </form>
    </div>
  );

  // Step 4: Success
  const renderSuccessStep = () => (
    <div className="w-full max-w-md">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-secondary-900 mb-2">
          Password Reset Successfully!
        </h2>
        <p className="text-secondary-600 mb-6">
          Your password has been reset. You can now login with your new password.
        </p>
        
        <div className="bg-white shadow-lg rounded-lg p-8">
          <p className="text-sm text-secondary-500 mb-4">
            Redirecting to login page in 3 seconds...
          </p>
          
          <Link
            to="/login"
            className="w-full btn-primary btn-lg inline-block text-center"
          >
            Go to Login
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding (same as login page) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 relative">
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4 text-white">
              Tokenised Bond Pilot Project
            </h1>
            <p className="text-xl text-blue-100">
              Secure password recovery for your account
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        {step === 1 && renderEmailStep()}
        {step === 2 && renderOtpStep()}
        {step === 3 && renderPasswordStep()}
        {step === 4 && renderSuccessStep()}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;

